import { useHistory, useLocation } from 'react-router-dom';
import { getInvitationInfoApi, getTimeZone, loginByIntegrationApi } from '@api/auth.api';
import './LoginPageFlows.scss';
import BingShortSVG from '@components/CampaignSettings/ContentSearch/ContentAutomationSearch/assets/BingShortSVG';

import QuestionTooltip from '@uikit/QuestionTooltip/QuestionTooltip';
import { LoginStep } from '@pages/LoginPage/LoginPage';
import { useGoogleLogin } from '@react-oauth/google';
import { addNotification } from '@redux/actions/notifications.actions';
import {
  AppSumoTokensResponseType,
  LoginResponseType,
  VerificationEmailResponseType,
} from '@ts/auth.types';
import { Button } from '@uikit/Form/Button/Button';
import Input from '@uikit/Form/Input/Input';
import PasswordScore from '@uikit/old/PasswordScore/PasswordScore';
import passwordValidation from '@utils/passwordValidation';
import { validateEmail } from '@utils/validations';
import cn from 'classnames';
import qs from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  AuthHandlerType,
  AuthHandlerTypeMap,
  VerificationCodeType,
} from 'respona_api/generated/auth-shared_pb';

// @ts-ignore
import googleIcon from 'src/assets/googleIcon.svg';
import { authService } from 'src/services/authService';
import { DispatchType } from 'src/store';
import Avatar from '@uikit/Avatar/Avatar';
import gtm from 'src/libs/gtm';
import WarningTriangleIcon from '@uikit/old/Icon/svgIcons/WarningTriangleIcon';
import { CodeType, RealmType } from 'respona_api/generated/common_pb';
import { isAppRealm, isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';
import { isStandaloneMode } from '@constants/app-modes';
import { setItemInStorage } from '@utils/localStorageHandler';
import urls from '@constants/urls';
import { SVGIcon } from '@uikit/old/Icon/Icon';
import {
  OrganizationOnboardingConciergeDetails,
  OrganizationOnboardingDetails,
} from 'respona_api/generated/user-organization_pb';
import { updateOrganizationOnboardingDetailsApi } from '@api/userOrganization.api';
import { redirectUserTo } from '@utils/historyHandler';

declare const IS_DEV: boolean;
declare const REALM: string;

type Props = {
  isLoading: boolean;
  handleInput?: (key: string, value: string | boolean) => void;
  updateRef?: (key: string, value: boolean) => void;
  onSubmit: (step?: LoginStep, response?: LoginResponseType) => void;
  history?: History;
  redirectUserTo?: (history: History, url) => void;
  state?: {
    email: string;
    password: string;
    fullName: string;
    code: string;
    captcha: string;
    newPassword: string;
    showCaptcha: boolean;
    type: AuthHandlerTypeMap[keyof AuthHandlerTypeMap];
  };
};

export function LoginSignupFlow(
  props: Props & {
    setIsLoading: (isLoading: boolean) => void;
    setLoginResponse: (response: LoginResponseType) => void;
    verification?: VerificationEmailResponseType;
    appSumoTokens?: AppSumoTokensResponseType | null;
    // AppSumo activation flow is in progress (a code was seen in the URL).
    appSumoActive?: boolean;
    // AppSumo flow is active but the code exchange failed: signup must not proceed,
    // otherwise the buyer gets a TRIAL org and the paid license is silently dropped.
    appSumoBlocked?: boolean;
  }
) {
  const dispatch = useDispatch<DispatchType>();
  const location = useLocation();
  const parsedSearch = qs.parse(location.search);
  const [googleHover, setGoogleHover] = useState<boolean>(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const { ref, src, ps_xid, code: appSumoCodeParam } = parsedSearch;

  useEffect(() => {
    if (ref && ref.length > 0) {
      setReferralCode(ref as string);
    }
  }, [ref]);

  useEffect(() => {
    if (ps_xid && ps_xid.length > 0) {
      setAffiliateCode(ps_xid as string);
    }
  }, [ps_xid]);

  const loginWithGoogle = useGoogleLogin({
    onSuccess: ({ code }) => {
      if (props.appSumoBlocked) {
        dispatch(
          addNotification({
            type: 'error',
            title:
              "Your AppSumo code couldn't be verified, so signup can't continue. Please return to AppSumo and open your activation link again.",
          })
        );
        return;
      }
      props.setIsLoading(true);
      loginByIntegrationApi(
        code,
        [
          { key: referralCode, type: CodeType.REFERRAL_CODE },
          { key: affiliateCode, type: CodeType.PARTNER_STACK },
          ...(props.appSumoTokens
            ? [
                { key: props.appSumoTokens.accessToken, type: CodeType.APP_SUMO_AUTH_TOKEN },
                { key: props.appSumoTokens.refreshToken, type: CodeType.APP_SUMO_REFRESH_TOKEN },
              ]
            : []),
        ],
        src as string
      )
        .then((result) => {
          props.setLoginResponse(result);
          if (result.firstLogin) {
            sessionStorage.removeItem('appsumoAuth');
            if (result.successMessage?.length > 0) {
              dispatch(addNotification({ type: 'success', title: result.successMessage }));
            }
            // Persist token before onboarding redirect so protected onboarding APIs succeed
            if (isStandaloneMode() && result.sessionToken) {
              setItemInStorage('token', result.sessionToken);
            }
            gtm.signUp({ userId: result.userId, email: result.userEmail, method: 'google' });
            gtm.signIn({ userId: result.userId, email: result.userEmail, method: 'google' });
            if (isAppRealm()) {
              props.redirectUserTo(props.history, `/${LoginStep.onboardingApp}`);
            } else if (isConciergeRealm()) {
              props.onSubmit(LoginStep.onboardingConciergeFirstOrder, result);
            } else if (isPublisherRealm()) {
              props.redirectUserTo(props.history, `/${LoginStep.onboardingPublisher}`);
            }
          } else {
            authService.loginCallback(props.history, result);
          }
        })
        .finally(() => props.setIsLoading(false));
    },
    onError: (errorResponse) => {
      console.error('Google Auth error', errorResponse);
    },
    flow: 'auth-code',
    scope: 'profile email',
  });

  const verificationFailed = props.verification?.valid === false;
  let subTitleText = 'Do-it-yourself email outreach';

  if (isConciergeRealm()) {
    subTitleText = 'Done-for-you placements';
  } else if (isPublisherRealm()) {
    subTitleText = 'Publisher portal';
  }

  const msRedirectionUrl = useMemo(() => {
    const refPart = ref ? `&ref=${ref}` : '';
    const srcPart = src ? `&src=${src}` : '';
    const pxdPart = ps_xid ? `&ps_xid=${ps_xid}` : '';
    const codePart = appSumoCodeParam ? `&app_code=${appSumoCodeParam}` : '';
    const parts = `${refPart}${srcPart}${pxdPart}${codePart}&realm=${REALM}`;
    const redirectUrl = IS_DEV ? 'dev.respona.com' : 'app.respona.com';
    return `https://${redirectUrl}/auth/microsoft/secure/aad?time-zone=${encodeURIComponent(getTimeZone())}${parts}`;
  }, [ref, src, ps_xid, appSumoCodeParam]);

  return (
    <div className="login-container-content">
      <div className="login-container__title">{subTitleText}</div>
      <div className="login-container__subtitle">
        By continuing, you agree to Respona’s{' '}
        <a
          className="login-container__subtitle-link"
          href="https://respona.com/terms-of-use/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Use
        </a>
        . Read our{' '}
        <a
          className="login-container__subtitle-link"
          href="https://respona.com/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
        .
      </div>

      <div
        className="login-container__button-column"
        // onMouseEnter={() => setGoogleHover(true)}
        // onMouseLeave={() => setGoogleHover(false)}
      >
        <Button
          className="login-container__button"
          onClick={() => loginWithGoogle()}
          disabled={props.isLoading}
        >
          {!googleHover ? (
            <>
              <img src={googleIcon} className="login-container__button-img" alt="google-icon" />
              Continue with Google
            </>
          ) : (
            <>
              <WarningTriangleIcon size={24} />
              <div className="login-container__button-text">
                Temporarily unavailable
                <span className="login-container__button-sub-text">
                  Please continue with “Work email” below
                </span>
              </div>
            </>
          )}
        </Button>

        <Button
          className="login-container__button"
          onClick={() => window.open(msRedirectionUrl, '_self')}
          // AppSumo activation: the one-time code was already redeemed by this page,
          // so the server-side Microsoft flow can't exchange it again. Route AppSumo
          // buyers through Google or work email instead of a guaranteed failure.
          disabled={props.isLoading || props.appSumoActive}
          title={
            props.appSumoActive
              ? 'Microsoft signup is not available for AppSumo activation. Please continue with Google or your work email.'
              : ''
          }
        >
          <div
            className="login-container__button-img"
            style={{
              width: 'auto',
              paddingLeft: '14px',
              marginRight: '13px',
            }}
          >
            <BingShortSVG size={30} />
          </div>
          Continue with Microsoft
        </Button>
      </div>

      {/* <div className="login-container__divider-row"> */}
      {/*  <div className="login-container__divider" /> */}
      {/*  <span>or continue with your email </span> */}
      {/*  <div className="login-container__divider" /> */}
      {/* </div> */}

      <div className="login-container__label">Work email</div>
      <Input
        type="email"
        name="email"
        className="login-container__input"
        data-testid="login-email"
        value={props.state.email}
        disabled={
          props.state.type === AuthHandlerType.GOOGLE ||
          props.state.type === AuthHandlerType.MICROSOFT
        }
        onKeyDown={(e) =>
          e.key === 'Enter' &&
          validateEmail(props.state.email) &&
          !verificationFailed &&
          props.onSubmit()
        }
        onChange={({ target: { value } }) => props.handleInput('email', value)}
        placeholder="name@domain.com"
        afterText={
          verificationFailed ? (
            <QuestionTooltip icon="warningTriangle" text={props.verification?.message} />
          ) : null
        }
      />

      <Button
        className="login-container__login-button"
        data-testid="login-continue"
        onClick={props.onSubmit}
        isLoading={props.isLoading}
        disabled={
          props.isLoading ||
          (!props.state.captcha && props.state.showCaptcha) ||
          !validateEmail(props.state.email) ||
          verificationFailed
        }
      >
        Continue
      </Button>
    </div>
  );
}

export function LoginFlow(props: Props) {
  const isDisabled = !validateEmail(props.state.email) || props.state.password?.length < 6;

  const onKeyDown = (e) => e.key === 'Enter' && !isDisabled && props.onSubmit();

  return (
    <div className="login-container-content">
      <div className="login-container__title">Welcome back</div>
      <div className="login-container__subtitle">
        Enter your password to log in to your Respona account.
      </div>

      <div className="login-container__input-column">
        <div className="login-container__label">Work email</div>
        <Input
          disabled
          className="login-container__input"
          onKeyDown={onKeyDown}
          value={props.state.email}
          onChange={({ target: { value } }) => props.handleInput('email', value)}
          placeholder="name@domain.com"
        />

        <div className="login-container__label">
          Password
          <span
            className="login-container__label-link"
            onClick={() => props.redirectUserTo(props.history, `/${LoginStep.forgotPassword}`)}
          >
            Forgot your password?
          </span>
        </div>
        <Input
          type="password"
          className="login-container__input login-container__input-password"
          data-testid="login-password"
          onKeyDown={onKeyDown}
          value={props.state.password}
          onChange={({ target: { value } }) => props.handleInput('password', value)}
          placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
        />
      </div>

      <Button
        className="login-container__login-button"
        data-testid="login-submit"
        onClick={props.onSubmit}
        isLoading={props.isLoading}
        disabled={(!props.state.captcha && props.state.showCaptcha) || isDisabled}
      >
        Log in
      </Button>

      {/* <div className="login-container__switch-step"> */}
      {/*  <span onClick={() => props.redirectUserTo(props.history, `/${LoginStep.welcome}`)}>Go back</span> */}
      {/* </div> */}

      <div className="login-container__switch-step">
        Don’t have an account?{' '}
        <span onClick={() => props.redirectUserTo(props.history, `/${LoginStep.welcome}`)}>
          Start for free
        </span>
      </div>
    </div>
  );
}

export function SignUpFlow(props: Props) {
  const { search } = useLocation();
  const { token } = qs.parse(search);

  const [isLoading, setIsLoading] = useState(Boolean(token));

  useEffect(() => {
    if (token) {
      setIsLoading(true);
      getInvitationInfoApi(token.toString())
        .then(({ email }) => {
          props.handleInput('email', email);
        })
        .finally(() => setIsLoading(false));
    }
  }, [token]);

  return (
    <div className="login-container-content">
      {token ? (
        <>
          <div className="login-container__title">Welcome to Respona</div>
          <div className="login-container__subtitle">
            Please enter your full name and password to join your team.
          </div>
        </>
      ) : (
        <>
          <div className="login-container__title">Create a new account</div>
          <div className="login-container__subtitle">
            Your login email can be different from the sending emails you connect to Respona.
          </div>
        </>
      )}

      <div className="login-container__input-column">
        <div className="login-container__label">Work email</div>
        <Input
          disabled
          type="email"
          name="email"
          className="login-container__input"
          value={props.state.email}
          // onChange={({ target: { value } }) => props.handleInput('email', value)}
          onChange={({ target: { value } }) => null}
          placeholder={isLoading ? 'Loading...' : 'name@domain.com'}
        />

        <div className="login-container__label">Full name</div>
        <Input
          className="login-container__input"
          name="name"
          value={props.state.fullName}
          onChange={({ target: { value } }) => props.handleInput('fullName', value)}
          placeholder="John Doe"
        />

        <div className="login-container__label">Password</div>
        <Input
          type="password"
          className="login-container__input"
          value={props.state.password}
          onChange={({ target: { value } }) => props.handleInput('password', value)}
          placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
        />
        <div className="login-container__input-message">
          <PasswordScore {...passwordValidation(props.state.password)} />
        </div>
      </div>

      <Button
        className="login-container__login-button"
        onClick={props.onSubmit}
        isLoading={props.isLoading}
        disabled={
          (!props.state.captcha && props.state.showCaptcha) ||
          !validateEmail(props.state.email) ||
          passwordValidation(props.state.password).score < 2 ||
          !props.state.fullName ||
          !props.state.fullName.trim() ||
          props.state.fullName.trim().split(/\s+/).length < 2
        }
        title={
          !validateEmail(props.state.email)
            ? 'Please enter a valid email address'
            : passwordValidation(props.state.password).score < 2
              ? 'Please enter a stronger password'
              : !props.state.fullName || !props.state.fullName.trim()
                ? 'Please enter your full name'
                : props.state.fullName.trim().split(/\s+/).length < 2
                  ? 'Please enter both first and last name'
                  : ''
        }
      >
        {token ? 'Continue' : 'Get verification code'}
      </Button>

      {/* Validation messages */}
      {!token && (
        <div className="login-container__validation-messages">
          {!validateEmail(props.state.email) && (
            <div className="login-container__validation-message">
              <WarningTriangleIcon size={16} />
              <span>Please enter a valid email address</span>
            </div>
          )}
          {validateEmail(props.state.email) &&
            props.state.password.length >= 6 &&
            passwordValidation(props.state.password).score < 2 && (
              <div className="login-container__validation-message">
                <WarningTriangleIcon size={16} />
                <span>Please enter a stronger password</span>
              </div>
            )}
          {validateEmail(props.state.email) &&
            passwordValidation(props.state.password).score >= 2 &&
            (!props.state.fullName ||
              !props.state.fullName.trim() ||
              props.state.fullName.trim().split(/\s+/).length < 2) && (
              <div className="login-container__validation-message">
                <WarningTriangleIcon size={16} />
                <span>
                  {!props.state.fullName || !props.state.fullName.trim()
                    ? 'Please enter your full name'
                    : 'Please enter both first and last name'}
                </span>
              </div>
            )}
        </div>
      )}

      {!token && (
        <div className="login-container__switch-step">
          Already have an account?{' '}
          <span onClick={() => props.redirectUserTo(props.history, `/${LoginStep.welcome}`)}>
            Log in
          </span>
        </div>
      )}
    </div>
  );
}

const options = [
  { id: 'google', label: 'Google', icon: 'search' },
  { id: 'chatgpt', label: 'ChatGPT', icon: 'chatGPTGrey' },
  { id: 'claude', label: 'Claude', icon: 'claude' },
  { id: 'facebook', label: 'Facebook', icon: 'facebook2' },
  { id: 'instagram', label: 'Instagram', icon: 'instagram2' },
  { id: 'linkedin', label: 'LinkedIn', icon: 'linkedin2' },
  { id: 'friend', label: 'Friend', icon: 'friends' },
  { id: 'other', label: 'Other', icon: 'more' },
];

const sourceFields = {
  friend: {
    label: 'Whom should we thank',
    placeholder: 'Their name',
  },
  other: {
    label: 'How did you find us?',
    placeholder: 'Please tell us more',
  },
};

export function ConciergeOnboardingScheduleCall(props: Props & { userEmail?: string }) {
  const history = useHistory();
  const location = useLocation();
  const avatarUrl = 'https://respona-public-files.s3.amazonaws.com/farzad.jpeg';

  const emailDomain = props.userEmail?.split('@')[1];

  const goNextStep = () => {
    redirectUserTo(history, `/${LoginStep.onboardingConciergeFirstOrder}`);
  };

  const showCalendly = () => {
    redirectUserTo(history, `${location.pathname}?isRightSideVisible=yes`);
  };

  return (
    <div className="login-container-content login-container--welcome-page login-container--schedule-call">
      <div className="login-container__title login-container__title--big">
        Don't guess on your first order.
      </div>
      <div className="login-container-content__subtitle">
        I'll research {emailDomain || 'your website'} before we talk and walk you through what I
        found.
      </div>
      <div className="login-container__list">
        <div className="login-container__list-item">
          <div className="login-container__list-item-avatar">
            <Avatar url={avatarUrl} fullName="" size="sm" />
            <div className="login-container__list-item-avatar-info">
              <p>Farzad Rashidi</p>
              <span>Founder of Respona</span>
            </div>
          </div>
        </div>
        <div className="login-container__list-item">
          <SVGIcon icon="check" size={9} color="#251FB6" />
          <p> Where competitors get mentioned in AI answers</p>
        </div>
        <div className="login-container__list-item">
          <SVGIcon icon="check" size={9} color="#251FB6" />
          <p>Which ones you can actually get into</p>
        </div>
        <div className="login-container__list-item">
          <SVGIcon icon="check" size={9} color="#251FB6" />
          <p>A first order with pages, tiers, and cost</p>
        </div>
        <div className="login-container__list-footer">
          <Button className="next-btn" onClick={goNextStep}>
            Skip & start my first order instead
          </Button>
          <Button className="pick-btn" rightIcon="arrowRightGrey" onClick={showCalendly}>
            Pick a Time
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ConciergeOnboardingTransitionFlow(props: Props) {
  const { redirectUserTo, history } = props;
  const expiredDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const [selectedSource, setSelectedSource] = useState<string>('');
  const [sourceComment, setSourceComment] = useState('');
  const [isDisabledNextButton, setIsDisabledNextButton] = useState(false);

  const onDetailsUpdate = () => {
    setIsDisabledNextButton(true);
    const realmReq = new OrganizationOnboardingConciergeDetails();
    const request = new OrganizationOnboardingDetails();
    realmReq.setFollowUpQuestion(sourceComment);
    realmReq.setReferralSource(selectedSource);
    request.setRealm(RealmType.RESPONA_CONCIERGE);
    request.setCompleted(true);
    request.setConcierge(realmReq);

    updateOrganizationOnboardingDetailsApi(request)
      .then(() => {
        redirectUserTo(history, urls.CONCIERGE_FIRST_ORDER);
      })
      .catch(() => {
        setIsDisabledNextButton(false);
      });
  };

  const sourceField = sourceFields[selectedSource];

  return (
    <div className="login-container-content login-container--welcome-page">
      <div className="login-container__title login-container__title--big">
        How did you hear about Respona?
      </div>
      <div className="login-container__survey">
        <div className="login-container__survey-label">Select an option:</div>
        <div className="login-container__survey-grid">
          {options.map((option) => (
            <Button
              colorIcon="#8E95AA"
              leftIcon={option.icon}
              key={option.id}
              type="bordered-grey"
              className={cn('login-container__survey-option', {
                'login-container__survey-option--active': selectedSource === option.id,
              })}
              onClick={() => {
                setSelectedSource(option.id);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {sourceField && (
          <div className="login-container__survey-field">
            <div className="login-container__label">{sourceField.label}</div>
            <Input
              value={sourceComment}
              onChange={({ target: { value } }) => setSourceComment(value)}
              placeholder={sourceField.placeholder}
            />
          </div>
        )}
      </div>

      <div className="login-container__footer">
        <div className="login-container__title-info">
          <div className="login-container__title-info__icon">
            <SVGIcon icon="gift" size={17} />
          </div>
          <div className="login-container__title-info__text">
            <span className="value">$25 welcome credit</span>
            <span className="date"> (expires in {expiredDate})</span>
          </div>
        </div>

        <Button
          className="login-container__login-button login-container__login-button--small"
          onClick={() => onDetailsUpdate()}
          isLoading={props.isLoading || isDisabledNextButton}
          disabled={!selectedSource}
        >
          Start your first order
        </Button>
      </div>
    </div>
  );
}

export function CodeConfirmationFlow(props: Props) {
  const dispatch = useDispatch<DispatchType>();

  const [resendDisabled, setResendDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const sendNewCode = () => {
    if (resendDisabled || isLoading) {
      return;
    }

    setIsLoading(true);
    authService
      .sendVerificationCode(props.state.email, VerificationCodeType.REGISTRATION)
      .then(() => {
        setResendDisabled(true);
        dispatch(addNotification({ title: 'New code is sent.', type: 'success' }));
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (resendDisabled) {
      const id = setTimeout(() => setResendDisabled(false), 10000);
      return () => clearTimeout(id);
    }
  }, [resendDisabled]);

  return (
    <div className="login-container-content">
      <div className="login-container__title">You’re almost signed up</div>
      <div className="login-container__subtitle">
        Enter the 6-digit code we just sent to your email to finish signing up.
      </div>

      <div className="login-container__input-column">
        <div className="login-container__label">
          Code sent to <div className="login-container__label-blue">{props.state.email}</div>
        </div>
        <Input
          className="login-container__input"
          value={props.state.code}
          onChange={({ target: { value } }) => props.handleInput('code', value)}
          placeholder="&bull;&bull;&bull;&bull;&bull;&bull;"
        />
      </div>

      <Button
        className="login-container__login-button"
        onClick={props.onSubmit}
        isLoading={props.isLoading}
        disabled={(!props.state.captcha && props.state.showCaptcha) || props.state.code.length < 6}
      >
        Verify email
      </Button>

      <div className="login-container__code-row">
        <div
          className={cn('login-container__code-button', {
            'login-container__code-button--disabled': resendDisabled || isLoading,
          })}
          onClick={sendNewCode}
        >
          Send a new code
        </div>
        <div
          className="login-container__code-button"
          onClick={() => props.redirectUserTo(props.history, '/welcome')}
        >
          Try a different email
        </div>
      </div>
    </div>
  );
}

export function OnboardingSessionFlow(props: Props) {
  const avatarUrl = 'https://respona-public-files.s3.amazonaws.com/farzad.jpeg';

  return (
    <div className="login-container-content">
      <div className="login-container__title">Onboarding webinar</div>
      <div className="login-container__subtitle">
        Attend a free onboarding session with the founder of Respona!
      </div>

      <div className="login-container__onboarding_session-block">
        <div className="login-container__onboarding_session-block__text">
          “In 30 mins, I’ll share little-known tips and strategies to help you get the most from
          your trial and see real results-fast.”
        </div>
        <div className="login-container__onboarding_session-block__farzad">
          <Avatar url={avatarUrl} fullName="" size="xm" />
          <div className="login-container__onboarding_session-block__farzad__text">
            <span className="login-container__onboarding_session-block__farzad__name">
              Farzad Rashidi
            </span>
            <span className="login-container__onboarding_session-block__farzad__position">
              Co-founder of Respona
            </span>
          </div>
        </div>
      </div>

      <Button
        className="login-container__login-button"
        onClick={() => {
          props.updateRef('setupDemo', true);
          props.onSubmit();
        }}
        isLoading={props.isLoading}
        disabled={false}
      >
        Schedule free onboarding webinar
      </Button>

      <div className="login-container__switch-step">
        <span
          onClick={() => {
            props.updateRef('setupDemo', false);
            props.onSubmit();
          }}
        >
          I’ve previously used Respona
        </span>
      </div>
    </div>
  );
}

export function RequestUpdatePasswordFlow(props: Props) {
  return (
    <div className="login-container-content">
      <div className="login-container__title">Update your password</div>
      <div className="login-container__subtitle" style={{ width: '304px' }}>
        Enter your login email to reset your password. We’ll send you the verification code.
      </div>

      <div className="login-container__input-column">
        <div className="login-container__label">Work email</div>
        <Input
          className="login-container__input"
          value={props.state.email}
          onChange={({ target: { value } }) => props.handleInput('email', value)}
          placeholder="name@domain.com"
        />
      </div>

      <Button
        className="login-container__login-button"
        onClick={props.onSubmit}
        isLoading={props.isLoading}
        disabled={
          (!props.state.captcha && props.state.showCaptcha) || !validateEmail(props.state.email)
        }
      >
        Get verification code
      </Button>

      <div className="login-container__switch-step">
        <span onClick={() => props.redirectUserTo(props.history, `/${LoginStep.welcome}`)}>
          Back to login
        </span>
      </div>
    </div>
  );
}

export function UpdatePasswordFlow(props: Props) {
  const dispatch = useDispatch<DispatchType>();

  const [resendDisabled, setResendDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const sendNewCode = () => {
    if (resendDisabled || isLoading) {
      return;
    }

    setIsLoading(true);
    authService
      .sendVerificationCode(props.state.email, VerificationCodeType.RESET_PASSWORD)
      .then(() => {
        setResendDisabled(true);
        dispatch(addNotification({ title: 'New code is sent.', type: 'success' }));
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (resendDisabled) {
      const id = setTimeout(() => setResendDisabled(false), 10000);
      return () => clearTimeout(id);
    }
  }, [resendDisabled]);

  return (
    <div className="login-container-content">
      <div className="login-container__title">Update your password</div>
      <div className="login-container__subtitle" style={{ width: '304px' }}>
        Enter the 6-digit code we just sent to your email to finish signing up.
      </div>

      <div className="login-container__input-column">
        <div className="login-container__label">
          Code sent to <div className="login-container__label-blue">{props.state.email}</div>
        </div>
        <Input
          className="login-container__input"
          value={props.state.code}
          onChange={({ target: { value } }) => props.handleInput('code', value)}
          placeholder="&bull;&bull;&bull;&bull;&bull;&bull;"
        />
        <div className="login-container__label">New Password</div>
        <Input
          type="password"
          className="login-container__input"
          value={props.state.newPassword}
          onChange={({ target: { value } }) => props.handleInput('newPassword', value)}
          placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
        />

        <div className="login-container__input-message">
          <PasswordScore {...passwordValidation(props.state.newPassword)} />
        </div>
      </div>

      <Button
        className="login-container__login-button"
        onClick={props.onSubmit}
        isLoading={props.isLoading}
        disabled={
          (!props.state.captcha && props.state.showCaptcha) ||
          props.state.code.length < 6 ||
          props.state.newPassword.length < 6
        }
      >
        Update password
      </Button>

      <div className="login-container__code-row">
        <div
          className={cn('login-container__code-button', {
            'login-container__code-button--disabled': resendDisabled || isLoading,
          })}
          onClick={sendNewCode}
        >
          Send a new code
        </div>
        <div
          className="login-container__code-button"
          onClick={() => props.redirectUserTo(props.history, `/${LoginStep.forgotPassword}`)}
        >
          Try a different email
        </div>
      </div>
    </div>
  );
}
